import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Award, Shield, Clock, HandHeart, Handshake, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { SITE } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2000&q=80";
const STORY_IMG = "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1400&q=80";

const VALUES = [
  { icon: Shield, title: "Integrity", text: "We do the right thing, every time, whether or not anyone is watching." },
  { icon: Award, title: "Excellence", text: "We hold ourselves to a high standard in every square foot we clean." },
  { icon: Clock, title: "Reliability", text: "When we make a commitment, we keep it." },
  { icon: HandHeart, title: "Respect", text: "We treat every property, client, and space with care." },
  { icon: Handshake, title: "Partnership", text: "We succeed when our clients succeed." },
];

export default function About() {
  useEffect(() => { document.title = "About | Eco Ready Solutions, LLC"; }, []);

  return (
    <div data-testid="page-about">
      <PageHero
        eyebrow="About Us"
        title="Built on Experience. Built on Trust."
        subtitle="Over 30 years of professional cleaning — and a genuine commitment to every property we serve."
        image={HERO_IMG}
      />

      {/* OUR STORY */}
      <section className="eco-section bg-white">
        <div className="eco-container grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <span className="eco-eyebrow">Our Story</span>
            <h2 className="eco-h2 mt-3">A Company Built on Genuine Clean</h2>
            <div className="mt-6 space-y-5 text-[17px] leading-[1.8] text-[#1A1A1A]">
              <p>
                Eco Ready Solutions, LLC was founded on a simple belief: that every property — whether it&apos;s a downtown
                office, a rental unit, or a family home — deserves to be genuinely clean. Not surface-level clean.
                Not rushed-through clean. But the kind of clean that you can feel the moment you walk through the door.
              </p>
              <p>
                That belief is backed by over 30 years of professional cleaning experience — decades spent learning what
                different spaces require, how to address the details others miss, and how to build the kind of trust
                that turns first-time clients into long-term partners.
              </p>
              <p>
                We are based in Royal Oak, Michigan, and we serve clients throughout Metro Detroit and Oakland County.
                Our work spans commercial offices, residential homes, rental properties, vacation rentals, and property
                management portfolios. No matter the size of the property or the scope of the job, we approach every
                engagement with the same level of professionalism and care.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-[14px] overflow-hidden border border-[#D6DDD9]">
              <img src={STORY_IMG} alt="Eco Ready Solutions team values" className="w-full h-[480px] object-cover" />
            </div>
            <blockquote className="eco-quote mt-6">
              &ldquo;We are in the business of partnerships, not transactions. Our goal is to become the cleaning partner
              you genuinely rely on — month after month, property after property.&rdquo;
            </blockquote>
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container max-w-4xl">
          <span className="eco-eyebrow">Experience</span>
          <h2 className="eco-h2 mt-3">30+ Years of Professional Experience</h2>
          <div className="mt-6 space-y-5 text-[17px] leading-[1.8] text-[#1A1A1A]">
            <p>
              Experience in the cleaning industry is not just measured in years — it is measured in problems solved,
              properties transformed, and clients who have learned they can depend on you. Our team brings that depth
              of knowledge to every job: understanding which products work best in which environments, how to approach
              a deep clean efficiently, what commercial clients need from a recurring program, and how to turn a rental
              unit quickly without cutting corners.
            </p>
            <p>
              When you hire Eco Ready Solutions, you are not hiring a company that is learning as it goes. You are
              hiring a team that has already seen the hard situations, developed the right approach, and built a track
              record of results.
            </p>
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="eco-section bg-white">
        <div className="eco-container max-w-4xl">
          <span className="eco-eyebrow">Our Commitment</span>
          <h2 className="eco-h2 mt-3">Our Commitment to You</h2>
          <div className="mt-6 space-y-5 text-[17px] leading-[1.8] text-[#1A1A1A]">
            <p>
              We are in the business of partnerships, not transactions. Our goal is not simply to complete a cleaning
              job — it is to become the cleaning partner that our clients rely on, month after month, property after
              property. That means showing up on time. Communicating when something changes. Following through on every
              detail in the scope. And genuinely caring about the outcome.
            </p>
            <p>
              We believe that the best measure of our work is a client who does not have to think about their cleaning —
              because they already know it is handled.
            </p>
          </div>
        </div>
      </section>

      {/* WHY WE BUILT */}
      <section className="eco-section" style={{ background: "#F4F6F5" }}>
        <div className="eco-container max-w-4xl">
          <span className="eco-eyebrow">Why We Built This</span>
          <h2 className="eco-h2 mt-3">Why We Built This Company</h2>
          <div className="mt-6 space-y-5 text-[17px] leading-[1.8] text-[#1A1A1A]">
            <p>
              Eco Ready Solutions, LLC was built because too many businesses and property owners struggle to find a
              cleaning company they can genuinely rely on. Missed appointments, inconsistent results, and poor
              communication are common complaints in this industry — and they should not be. We built this company to
              be different: structured, professional, dependable, and genuinely invested in the properties we serve.
            </p>
            <p>
              We also believe in the value of local business. We are proud to be based in Royal Oak and to serve our
              neighbors across Metro Detroit and Oakland County. Every contract we earn is an opportunity to show what
              a truly professional cleaning operation looks like from the inside out.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="eco-section bg-white">
        <div className="eco-container">
          <div className="max-w-2xl">
            <span className="eco-eyebrow">Core Values</span>
            <h2 className="eco-h2 mt-3">Our Core Values</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} data-testid={`value-card-${i}`} className="eco-card">
                  <span className="eco-icon-wrap"><Icon size={22} /></span>
                  <h3 className="text-[17px] font-semibold mt-5">{v.title}</h3>
                  <p className="mt-2 text-[#555555] text-[14.5px] leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="eco-section text-white" style={{ background: "#1A3D2E" }}>
        <div className="eco-container text-center max-w-3xl mx-auto">
          <p className="text-white/90 text-[18px] md:text-[19px] leading-[1.75]">
            Eco Ready Solutions, LLC is ready to serve your property, your business, and your guests — with the
            professionalism, experience, and care that every space deserves.
          </p>
          <div className="mt-8">
            <Link to="/contact" data-testid="about-cta-contact" className="eco-btn"
              style={{ background: "#fff", color: "#2D6A4F" }}>
              Let&apos;s Be Clean Together — Contact Us <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
